import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Medal, Users, Mic, BookOpen } from "lucide-react";
import { awards as awardsMock, publications as pubsMock, images } from "../mock/mock";
import { useCollection } from "../hooks/useCollection";

const communityLeadership = [
  { t: "Organizer — GDG Cloud Gandhinagar", c: "Co-leading Google Developer Group activities focused on cloud, AI, and modern engineering." },
  { t: "Developer Community Growth", c: "Built and scaled technical communities across institutions and industry forums." },
  { t: "Workshops & Learning Initiatives", c: "Authored structured workshop material enabling engineers and students to go production-ready." },
];

const speakingHighlights = [
  "DevFest Ahmedabad — Expert Talk on Generative AI",
  "GDG Cloud Gandhinagar — Keynote",
  "TST Technology — Podcast feature",
  "Project Perks with Harkiran — Agile Leadership",
  "Ayushi Rawat Talk Show — Cyber & Career",
];

export default function Awards() {
  const { data: awards } = useCollection("awards", awardsMock);
  const { data: publications } = useCollection("publications", pubsMock);
  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO */}
      <section className="pt-16 md:pt-24 pb-14 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="tag-mono mb-6">[ Awards · Recognition · Contribution ]</div>
            <h1 className="headline-xl">
              Recognition, <span className="italic-serif text-[var(--molten)]">impact</span>, and contribution.
            </h1>
            <p className="mt-8 text-lg text-[var(--ink)]/75 max-w-2xl leading-relaxed">
              A curated record of honours, community leadership, speaking influence, and publications — earned through consistent, measurable work.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <img src={images.awardTrophy} alt="Award" className="w-full h-72 object-cover" />
          </div>
        </div>
      </section>

      {/* AWARDS LIST */}
      <section className="py-16 md:py-20 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="tag-mono mb-3">[ Honours &amp; Awards ]</div>
              <h2 className="headline-lg">
                Consistently <span className="italic-serif text-[var(--molten)]">recognized.</span>
              </h2>
            </div>
            <div className="tag-mono">{awards.length} recognitions · 2023 — 2025</div>
          </div>

          <div className="border-t border-[var(--ink)]/15">
            {awards.map((a, i) => (
              <a
                key={i}
                href={a.link}
                target="_blank"
                rel="noreferrer"
                className="rise group grid grid-cols-12 gap-6 py-8 md:py-10 border-b border-[var(--ink)]/15 hover:bg-[var(--cream-soft)] px-2 -mx-2 transition-colors"
              >
                <div className="col-span-2 md:col-span-1 font-serif text-3xl md:text-4xl">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-10 md:col-span-2 tag-mono pt-3">{a.year}</div>
                <div className="col-span-12 md:col-span-8">
                  <div className="flex items-start gap-3">
                    <Medal size={20} className="mt-1 text-[var(--molten)] shrink-0" />
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl leading-tight group-hover:text-[var(--molten)] transition-colors">{a.title}</h3>
                      <p className="mt-3 text-[var(--ink)]/70 leading-relaxed max-w-2xl">{a.body}</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end items-center">
                  <ArrowUpRight size={22} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY LEADERSHIP */}
      <section className="section-ink grain py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-12">
            <div className="col-span-12 md:col-span-8">
              <div className="tag-mono mb-4">[ Community Leadership ]</div>
              <h2 className="headline-lg text-[var(--cream)]">
                Building ecosystems, not just <span className="italic-serif text-[var(--molten)]">audiences.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right tag-mono">
              <Users size={18} className="inline mr-2 text-[var(--molten)]" /> Google for Developers recognized
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[var(--cream)]/20">
            {communityLeadership.map((c, i) => (
              <div
                key={i}
                className={`rise p-8 md:p-10 border-b border-[var(--cream)]/20 ${
                  i !== communityLeadership.length - 1 ? "md:border-r" : ""
                }`}
              >
                <Users size={22} className="text-[var(--molten)] mb-6" />
                <h3 className="font-serif text-2xl text-[var(--cream)] leading-snug">{c.t}</h3>
                <p className="mt-4 text-[var(--cream)]/70 leading-relaxed">{c.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPEAKING & INFLUENCE */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="tag-mono mb-4">[ Speaking &amp; Influence ]</div>
              <h2 className="headline-lg">
                Keynotes, talks, and long-form <span className="italic-serif text-[var(--molten)]">conversations.</span>
              </h2>
              <p className="mt-6 text-[var(--ink)]/75 max-w-md leading-relaxed">
                Delivered sessions on AI, DevOps, cybersecurity, and engineering leadership across industry events and podcasts.
              </p>
              <Link to="/speaking" className="mt-8 btn-ink">See speaking <Mic size={15} /></Link>
            </div>

            <div className="col-span-12 md:col-span-7">
              <ul className="border-t border-[var(--ink)]/15">
                {speakingHighlights.map((s, i) => (
                  <li key={i} className="rise py-5 border-b border-[var(--ink)]/15 flex items-center gap-5">
                    <span className="font-serif text-xl text-[var(--molten)]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-xl md:text-2xl">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PUBLICATIONS */}
      <section className="py-24 md:py-32 bg-[var(--cream-soft)] grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap justify-between items-end gap-6 mb-12">
            <div>
              <div className="tag-mono mb-4">[ Publications &amp; Contributions ]</div>
              <h2 className="headline-lg">
                Research and <span className="italic-serif text-[var(--molten)]">peer-reviewed</span> work.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {publications.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="rise p-7 border border-[var(--ink)]/15 bg-[var(--cream)] hover:border-[var(--molten)] transition-colors group"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2 tag-mono">
                    <BookOpen size={14} /> {p.venue}
                  </div>
                  <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl leading-snug">{p.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 grain">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <div className="tag-mono mb-4 justify-center flex">[ Work with me ]</div>
          <h2 className="headline-lg max-w-3xl mx-auto">
            If you want this kind of track record inside your organisation —{" "}
            <span className="italic-serif text-[var(--molten)]">let’s talk.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-ink">Book a conversation <ArrowUpRight size={16} /></Link>
            <Link to="/contact" className="btn-outline">Invite me to speak <Mic size={15} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
